import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select, {
  components,
  FormatOptionLabelMeta,
  MenuPlacement,
  Props as SelectProps,
  createFilter,
} from "react-select";
import CreatableSelect from "react-select/creatable";
// Material-UI imports
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import classNames from "classnames";
import _ from "lodash";
// Checkbox mode imports
import Checkbox from "@mui/material/Checkbox";
// Formik imports
import { FieldMetaProps } from "formik";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { FieldError } from "./FieldError";
import { useTheme } from "@mui/material";

export interface ISelectFieldProps extends Omit<SelectProps, "isOptionDisabled"> {
  name: string;
  value?: any;
  "data-testid"?: string;
  label?: string;
  className?: string;
  multiMode?: string;
  maxOptionsShown?: number;
  maxMenuHeight?: number;
  hideSelectedValues?: boolean;
  placeholder?: string;
  options: any[];
  getOptionLabel?: (option: any) => string;
  // Fixes issue where SelectProps assigns these as unknown and conflicts with our known types.
  formatOptionLabel?: (option: any, formatOptionLabelMeta: FormatOptionLabelMeta<any>) => ReactNode;
  getOptionValue?: (option: any) => string;
  // Fixes issue where SelectProps assigns these as unknown and conflicts with our known types.
  isOptionDisabled?: (option: any, selectValue: any[]) => boolean;
  onChange?: (option: any) => any;
  meta?: Partial<FieldMetaProps<any>>;
  // when passed, uses a special version of the Select component which allows for creating new entries.
  onCreateOption?: (inputValue: string) => void;
  creatable?: boolean;
  disabled?: boolean;
  style?: any;
  valueKey?: any;
  labelSx?: any;
  filterOptions?: any;
}

const getMenuHeightLimit = (defaultMenuState: boolean, selectRef: any, forceCheck?: boolean): null | number => {
  // If any window is scrolling, force an update of this component to reposition it's menu.
  const currentMaxMenuHeight = selectRef?.props?.maxMenuHeight;
  const currentMenuPlacement = selectRef?.props?.menuPlacement;
  const currentmenuIsOpen = forceCheck ?? selectRef?.props?.menuIsOpen;
  // If menu isn't being used in default open state, dynamic change it's height if available space requires it.
  if (!defaultMenuState && currentmenuIsOpen) {
    const controlRef: any = selectRef?.controlRef;
    const currentSelectRect = controlRef?.getBoundingClientRect();
    const bodyRect = document.body.getBoundingClientRect();
    if (currentSelectRect && bodyRect) {
      // const spaceFromTop = bodyRect.height - currentSelectRect.top - 50;
      const availableSpaceAbove = currentSelectRect.top;
      const availableSpaceBelow = bodyRect.height - currentSelectRect.bottom - 50;
      // console.log("availableSpaceAbove", availableSpaceAbove);
      // console.log("availableSpaceBelow", availableSpaceBelow);
      if (currentMenuPlacement === "bottom" && currentMaxMenuHeight > availableSpaceBelow) {
        return availableSpaceBelow;
      } else if (currentMenuPlacement === "top" && currentMaxMenuHeight > availableSpaceAbove) {
        return availableSpaceAbove;
      } else if (
        currentMenuPlacement === "auto" &&
        (currentMaxMenuHeight > availableSpaceBelow || currentMaxMenuHeight > availableSpaceAbove)
      ) {
        return Math.max(availableSpaceBelow, availableSpaceAbove);
      }
      return null;
    }
  }
  return null;
};

// SELECTFIELD COMPONENT
export const SelectField = (props: ISelectFieldProps): JSX.Element => {
  const {
    label,
    name,
    "data-testid": dataTestId,
    value,
    onFocus,
    onBlur,
    onChange,
    meta,
    className,
    multiMode,
    maxOptionsShown,
    disabled,
    isMulti,
    // placeholder,
    onCreateOption,
    creatable = true,
    maxMenuHeight,
    menuPortalTarget,
    labelSx,
    filterOptions,
  } = props;
  const selectMenuRef = useRef();
  const currentSelect = selectMenuRef?.current ?? null;
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const defaultMenuState = props?.menuIsOpen ?? false;

  const selectRef = useMemo(() => (currentSelect as any)?.select, [currentSelect]);
  const [limitMenuHeight, setLimitMenuHeight] = useState<number>(null);

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { value, initialValue, touched } = meta;
    return touched && !_.isEqual(value, initialValue) ? true : false;
  }, [meta]);

  const alwaysShowPlaceholder = useCallback((props: any): JSX.Element => {
    const placeholder = props?.selectProps?.placeholder ?? "";
    if (!!props.children && props.children.length > 1) {
      return (
        <div className={classNames("react-select__value-container, hide_selected_items")}>
          <div className="react-select__placeholder forced">{placeholder}</div>
          {props.children}
        </div>
      );
    }
    return null;
  }, []);

  /** If Selector is set to multi, make sure it always returns an empty array when empty */
  const returnCorrectType = useCallback((fieldoutput: any) => (isMulti && !fieldoutput ? [] : fieldoutput), [isMulti]);

  /** If multimode is set to checkbox, ensure the follow props are passed as such. */
  const multiModeProps = useMemo(() => {
    return multiMode === "checkbox"
      ? { hideSelectedValues: maxOptionsShown ? false : true, hideSelectedOptions: false }
      : {};
  }, [multiMode, maxOptionsShown]);

  const newComponents = useMemo(() => {
    // If selection tool is in checkbox mode, renders checkboxes in the selection option menu.
    const checkboxMode = (props: any): JSX.Element => {
      return (
        <components.Option {...props}>
          <Checkbox className={"option_checkbox"} checked={props.isSelected} />
          {props.label}
        </components.Option>
      );
    };
    if (multiMode === "checkbox") {
      const newComps = { Option: checkboxMode };
      if (maxOptionsShown && value.length <= maxOptionsShown) return newComps;
      else return { ...newComps, ValueContainer: alwaysShowPlaceholder };
    }
    return null;
  }, [multiMode, maxOptionsShown, alwaysShowPlaceholder, value]);

  const theme = useTheme();
  const customStyles = {
    menuPortal: (base: any) => {
      const portalTop = base.top;
      const displayPortal = portalTop < 36 ? "none" : "block";
      return { ...base, zIndex: 999999, top: portalTop, display: displayPortal };
    },

    menu: (styles: any, state: any) => {
      const checkBoxes: boolean = multiMode === "checkbox" ? true : false;
      const maxHeight: number = limitMenuHeight ? limitMenuHeight : (maxMenuHeight ?? 400);
      const selectedBackgroundColor = checkBoxes
        ? theme.palette.background.paper
        : `${theme.palette.primary.main} !important`;
      const menuStyles = {
        ...styles,
        borderRadius: 0,
        fontSize: 12,
        border: "1px solid " + theme.custom.forms.inputBorder,
        backgroundColor: theme.palette.background.paper,
        color: theme.typography.body2.color,
        top: "auto",
        "& .react-select__menu-list": {
          padding: "5px",
          maxHeight: maxHeight,
          "& .react-select__option": {
            padding: "5px",
            fontSize: 12,
            fontWeight: 300,
            color: theme.custom.forms.labels,
            "& span.option_checkbox": {
              padding: 0,
              margin: "0px 10px 0px 0px",
            },
            "&.react-select__option--is-focused": {
              backgroundColor: theme.custom.forms.inputBack,
              color: theme.custom.modal.title,
            },
            "&.react-select__option--is-selected": {
              backgroundColor: selectedBackgroundColor,
              color: theme.palette.primary.contrastText,
              "& span.option_checkbox": { color: theme.palette.primary.main },
            },
          },
        },
      };
      return menuStyles;
    },
  };

  const handleFocus = (e: React.FocusEvent<HTMLElement>): void => {
    setIsFocused(true);
    // Uses props onfocus (as formik doesn't supply one)
    if (onFocus && typeof onFocus === "function") {
      onFocus(e as React.FocusEvent<HTMLInputElement>);
    }
  };

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = (e: React.FocusEvent<Element>): void => {
    e.persist();
    setIsFocused(false);
    setTimeout(() => {
      if (onBlur && typeof onBlur === "function") {
        onBlur(e as React.FocusEvent<HTMLInputElement>);
      }
    }, 1);
  };

  /**
   * OnChange handler sends the value back up to the parent.
   * @param outputValue - selected option(s) from the menu.
   */
  const handleOnChange = (outputValue: any): void => {
    if (onChange && typeof onChange === "function") {
      onChange(returnCorrectType(outputValue));
    }
  };

  // Calculates the available space above and below the menuList dom reference (in relation to the body)
  // Uses the maxMenu size as opposed to the listMenu reference clientHeight (as it proved problematic)
  // Renders the menu above or below the input components based on this available space.
  const menuPlacement: MenuPlacement = useMemo(() => {
    if (!currentSelect || !(currentSelect as any)?.select?.controlRef) {
      return "auto";
    }
    const menuHeight = maxMenuHeight ?? 400;
    const controlRef: any = (currentSelect as any)?.select?.controlRef;
    const bodyRect = document.body.getBoundingClientRect();
    const currentSelectRect = controlRef.getBoundingClientRect();
    const availableMenuSpaceBelow: boolean = bodyRect.bottom - currentSelectRect.bottom > menuHeight;
    const availableMenuSpaceAbove: boolean = currentSelectRect.top > menuHeight;
    if (availableMenuSpaceBelow || (!availableMenuSpaceAbove && !availableMenuSpaceBelow)) {
      return "bottom";
    }
    return "top";
  }, [maxMenuHeight, currentSelect]);

  const hasError = meta?.error ? true : false;

  //! setting select value as undefined doesn't reset it's internal state
  // HACK if undefined is passed in as a value, assign null as the internal value to the selector.
  const internalValue = value ? value : !value && isMulti ? [] : null;

  const selectFieldStyles = (theme: any, labelSx: any): any => ({
    root: {
      display: "flex",
      flexDirection: "column",
      margin: "5px",
      flexWrap: "wrap",
      width: "calc(100% - 10px)",
      "& .MuiFormLabel-root": {
        overflowWrap: "normal !important",
        whiteSpace: "normal !important",
        overflow: "initial !important",
        textOverflow: "initial !important",
        maxWidth: "unset !important",
      },
      "& .hideMuiLabel .MuiFormLabel-root": {
        display: "none !important",
      },
      "&.auto_width .input_control label": { width: "auto" },
      "& .input_control": {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        "& label": {
          position: "relative",
          width: "80px",
          textAlign: "right",
          transform: "none",
          color: theme.custom.forms.pristineColor,
          transition: theme.transitions.create(["border-color", "color"]),
          fontSize: 12,
          lineHeight: "12px",
          margin: "5px 10px auto auto",
          ...labelSx,
        },
        "& .react-select-container": {
          flexGrow: 1,
          "& .react-select__control": {
            cursor: "text",
            marginTop: 0,
            minWidth: "100px",
            overflow: "auto",
            maxHeight: "200px",
            minHeight: "20px",
            borderRadius: "2px",
            position: "relative",
            color: theme.custom.forms.inputText,
            backgroundColor: theme.custom.forms.inputBack,
            border: "1px solid " + theme.custom.forms.inputBorder,
            fontSize: 12,
            transition: theme.transitions.create(["border-color", "color"]),
            "&.react-select__control--is-focused": {
              "& .react-select__placeholder": { display: "none" },
              "& .react-select__placeholder.forced": { display: "none" },
            },
            "&:hover": {
              borderColor: theme.custom.forms.pristineColor,
            },
            "& .hide_selected_items": {
              display: "flex",
              margin: "2px 0px 2px 8px",
              "& .react-select__multi-value, & .react-select__single-value, & .react-select__placeholder": {
                display: "none",
              },
              "& .react-select__input-container": { margin: 0, padding: 0 },

              "& .react-select__placeholder.forced": {
                display: "flex",
                position: "relative",
                transform: "none",
                height: "16px",
                lineHeight: "16px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            },
            "& .react-select__input": { color: `${theme.custom.forms.inputText} !important` },
            "& .react-select__indicators": {
              height: "20px",
              margin: "1px 0px",
              "& div": {
                color: theme.custom.forms.pristineColor,
                padding: 0,
              },
              "& span": { margin: "2px 0px", backgroundColor: theme.custom.forms.inputBorder },
              "& svg": {
                cursor: "pointer",
                "&:hover": { color: theme.custom.forms.inputText },
              },
            },
            "& .react-select__placeholder": {
              height: "16px",
              lineHeight: "16px",
              fontWeight: 300,
              color: theme.custom.forms.labels,
            },
            "& .react-select__value-container": {
              padding: "0px 8px",
              "& .react-select__single-value": {
                width: "100%",
                fontWeight: 300,
                color: theme.custom.forms.inputText,
                lineHeight: "20px",
              },
              "& div": { paddingTop: 0, paddingBottom: 0 },
            },
            "& .react-select__value-container--is-multi": {
              "& .react-select__multi-value": {
                maxWidth: "124px",
                backgroundColor: theme.custom.forms.inputText,
                margin: "1px",
                "& .react-select__multi-value__label": {
                  fontWeight: 400,
                  fontSize: 10,
                  lineHeight: "16px",
                  padding: "0px 6px",
                  color: theme.custom.forms.inputBack,
                },
                "& .react-select__multi-value__remove": {
                  padding: 0,
                  margin: "2px",
                  color: theme.custom.forms.inputBack,
                  "& svg": { height: "12px", width: "12px" },
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: theme.custom.forms.inputBack,
                    "& svg": { fill: theme.custom.forms.inputText },
                  },
                },
              },
            },
          },
          "& .react-select__control--is-focused": {
            boxShadow: "none",
          },
          "& .react-select__menu": {
            zIndex: 2000,
            backgroundColor: theme.custom.forms.inputBack,
            fontWeight: 400,
            fontSize: 12,
            "& .react-select__option--is-focused": {
              backgroundColor: theme.palette.background.paper,
            },
          },
          "&:after": {
            content: '""',
            transition: theme.transitions.create(["transform"]),
            left: 0,
            right: 0,
            bottom: 0,
            position: "absolute",
            transform: "scaleX(0)",
            borderBottom: "2px solid " + theme.custom.forms.focusedColor,
            pointerEvents: "none",
          },
        },
      },
      "&.disabled .input_control .react-select-container": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.error .input_control": {
        "& label": {
          color: theme.custom.forms.errorColor + "!important",
        },
        "& .react-select-container": {
          "&:after": {
            transform: "scaleX(1)",
            borderBottom: "2px solid " + theme.custom.forms.errorColor,
          },
        },
      },
      "&.dirty .input_control": {
        "& label": {
          color: theme.custom.forms.dirtyColor,
        },
        "& .react-select-container": {
          "& .react-select__control": {
            border: "1px solid " + theme.custom.forms.dirtyColor,
          },
        },
      },
      "&.focused .input_control": {
        "& label": {
          color: theme.custom.forms.focusedColor,
        },
        "& .react-select-container": {
          "& .react-select__control": {
            border: "1px solid " + theme.custom.forms.pristineColor,
          },
          "&:after": {
            transform: "scaleX(1)",
          },
        },
      },
      "& .error-message": {
        maxWidth: 300,
        padding: "0px 5px",
        fontWeight: 300,
        color: theme.palette.error.main,
      },
    },
  });

  return (
    <FormControl
      data-testid={dataTestId}
      className={classNames(
        className,
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error",
        multiMode === "checkbox" && "checkbox_mode"
      )}
      sx={(theme) => selectFieldStyles(theme, labelSx).root}
      error={hasError}
    >
      <div className="input_control" style={props?.style ?? {}}>
        {!!label && <InputLabel htmlFor={"id-" + name}>{label}</InputLabel>}
        {onCreateOption && creatable ? (
          //@ts-ignore - Added to resolve a conflict in the type passed in for isOptionDisabled
          <CreatableSelect
            {...props}
            data-testid={dataTestId}
            id={name + "_selection"}
            inputId={name}
            ref={selectMenuRef}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPlacement={props?.menuPlacement ?? menuPlacement}
            menuPortalTarget={menuPortalTarget}
            menuShouldBlockScroll={false}
            menuShouldScrollIntoView={true}
            closeMenuOnScroll={true}
            closeMenuOnSelect={!!multiMode || isMulti ? false : true}
            styles={customStyles}
            value={internalValue}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleOnChange}
            onCreateOption={onCreateOption}
            components={{ ...components, ...newComponents }}
            filterOption={filterOptions ? createFilter(filterOptions) : undefined}
            onMenuOpen={() => {
              if (selectRef) {
                const newLimit = getMenuHeightLimit(defaultMenuState, selectRef, true);
                setLimitMenuHeight(newLimit);
              }
            }}
            {...multiModeProps}
          />
        ) : (
          //@ts-ignore - Added to resolve a conflict in the type passed in for isOptionDisabled
          <Select
            {...props}
            data-testid={dataTestId}
            id={name + "_selection"}
            inputId={name}
            ref={selectMenuRef}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPlacement={props?.menuPlacement ?? menuPlacement}
            menuPortalTarget={menuPortalTarget}
            menuShouldBlockScroll={false}
            menuShouldScrollIntoView={true}
            closeMenuOnScroll={true}
            closeMenuOnSelect={!!multiMode || isMulti ? false : true}
            styles={customStyles}
            value={internalValue}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleOnChange}
            components={{ ...components, ...newComponents }}
            filterOption={filterOptions ? createFilter(filterOptions) : undefined}
            onMenuOpen={() => {
              if (selectRef) {
                const newLimit = getMenuHeightLimit(defaultMenuState, selectRef, true);
                setLimitMenuHeight(newLimit);
              }
            }}
            {...multiModeProps}
          />
        )}
      </div>
      <FieldError meta={meta} />
    </FormControl>
  );
};
